import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useLogo } from '../../context/LogoContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Home, Send, Users, Clock, Calculator, FileCheck, User, HelpCircle, LogOut, Menu, X, Wallet, Bell, FileText, CreditCard, Gift } from 'lucide-react';
import { useState } from 'react';
import LanguageSelector from '../LanguageSelector';
import NotificationBell from '../NotificationBell';

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const { logoUrl } = useLogo();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { icon: Home, label: t('nav_dashboard'), path: '/dashboard' },
    { icon: Wallet, label: t('nav_deposit'), path: '/deposit' },
    { icon: Send, label: t('nav_send'), path: '/send' },
    { icon: Users, label: t('nav_recipients'), path: '/recipients' },
    { icon: Clock, label: t('nav_history'), path: '/history' },
    { icon: Calculator, label: t('nav_exchange'), path: '/exchange' },
    { icon: FileCheck, label: t('nav_kyc'), path: '/kyc' },
    { icon: FileText, label: t('nav_income_cert'), path: '/income-certificate' },
    { icon: CreditCard, label: t('nav_residence_card'), path: '/residence-card' },
    { icon: Gift, label: t('nav_referral'), path: '/referral' },
    { icon: Bell, label: t('nav_notifications'), path: '/notifications' },
    { icon: User, label: t('nav_profile'), path: '/profile' },
    { icon: HelpCircle, label: t('nav_support'), path: '/support' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/50 bg-white/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2" data-testid="dashboard-logo">
              <img src={logoUrl} alt="PRC Remit" className="h-9 w-9 rounded-full object-cover" />
              <span className="font-logo text-xl font-bold text-primary">PRC Remit</span>
            </Link>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <LanguageSelector />
              <div className="hidden md:block text-sm">
                <div className="text-primary font-medium">{user?.full_name}</div>
                <div className="text-muted-foreground text-xs">{user?.email}</div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="hidden md:flex gap-2 rounded-full"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
                {t('logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="md:hidden border-b border-border bg-card" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg smooth-transition ${
                  location.pathname === item.path
                    ? 'bg-accent/10 text-accent font-medium'
                    : 'text-muted-foreground hover:bg-secondary'
                }`}
                data-testid={`mobile-nav-${item.path}`}
              >
                <item.icon className="h-5 w-5" />
                {item.label}
              </Link>
            ))}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:bg-secondary smooth-transition"
              data-testid="mobile-logout-button"
            >
              <LogOut className="h-5 w-5" />
              {t('logout')}
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <aside className="hidden md:block">
            <nav className="space-y-2 sticky top-24">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg smooth-transition ${
                    location.pathname === item.path
                      ? 'bg-accent/10 text-accent font-medium'
                      : 'text-muted-foreground hover:bg-secondary'
                  }`}
                  data-testid={`nav-${item.path}`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Link>
              ))}
            </nav>
          </aside>
          <main className="md:col-span-3">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
